import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import {
  DndContext,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import OrgMemberAvatar from "@/components/avatars/OrgMemberAvatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, GripVertical, Calendar, Pencil, EyeOff, ArrowRightLeft, Paperclip, X, Trash2 } from "lucide-react";
import LinkedParentDialog from "@/components/LinkedParentDialog";
import { getFullName } from "@/lib/userUtils";
import { toastError } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { plainExcerpt } from "@/lib/markdownPlain";
import { kanbanCardAnchorId } from "@/lib/permalinks";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { FEED_SILENT_TOOLTIP } from "@/lib/kanbanConstants";
import { useAuth } from "@/scripts/AuthContext";
import {
  workKindOf,
  workKindLabel,
  parentIdOf,
  storyPointsOf,
  childCountsOf,
  postIdOfWork,
  isUnderEpic,
  isUnderWorkParent,
  parentChainOf,
  epicHorizonOf,
  cardDueAt,
  cardStartsAt,
  cardLaneOf,
  isCardOverdue,
  linkedWorkSet,
  sortLinkedWork,
} from "@/lib/workKind";
import CardInspectOverlay, {
  isCardFrozen,
  cardAssignees,
  ParentBreadcrumb,
} from "@/components/CardInspectOverlay";
import { useIsMobile } from "@/hooks/use-mobile";

function boardWorkFilterKey(orgId, teamId) {
  return `circa.kanban.workFilter.${orgId || "org"}.${teamId || "team"}`;
}

function loadBoardWorkFilter(orgId, teamId) {
  try {
    const raw = localStorage.getItem(boardWorkFilterKey(orgId, teamId));
    if (!raw) return { showEpics: false, epicFilter: "all" };
    const parsed = JSON.parse(raw);
    return {
      showEpics: Boolean(parsed?.showEpics),
      epicFilter:
        typeof parsed?.epicFilter === "string" ? parsed.epicFilter : "all",
    };
  } catch {
    return { showEpics: false, epicFilter: "all" };
  }
}

// Default kanban column statuses (fallback if lanes not provided)
const DEFAULT_KANBAN_STATUSES = ["Backlog", "To Do", "In Progress", "In Review", "Done"];
const HANDED_OFF_LANE = "Handed off";

const EPIC_HORIZON_STRIPS = [
  { key: "now", label: "Now" },
  { key: "upcoming", label: "Upcoming" },
  { key: "done", label: "Done" },
];

function LinkedDoneChip({ card, onShowLinkedWork }) {
  const counts = childCountsOf(card);
  if (!counts) return null;
  return (
    <Badge
      variant="outline"
      className="text-xs font-normal cursor-pointer hover:bg-muted"
      title="Show linked stories and tasks on this board"
      onClick={(e) => {
        e.stopPropagation();
        if (onShowLinkedWork) onShowLinkedWork(card);
      }}
    >
      {counts.done} of {counts.total} done
    </Badge>
  );
}

// Sortable Kanban Card Component
const SortableKanbanCard = ({
  card,
  onCardClick,
  onCardEdit,
  onShowLinkedWork,
  onOpenRemoteParent,
  isHighlighted,
  completedWorkLane = null,
  hierarchyCards = [],
  canMoveCards = true,
  /** When set, show an explicit lane picker (mobile list) instead of relying on DnD. */
  laneOptions = null,
  onMoveToLane = null,
  listMode = false,
}) => {
  const frozen = isCardFrozen(card);
  const dragDisabled = frozen || !canMoveCards || listMode;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: card.post_id || card.postId,
    disabled: dragDisabled,
  });

  const style = listMode
    ? undefined
    : {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      };
  const currentLane = card.kanbanStatus || card.kanban_status || "";

  const due = cardDueAt(card);
  const isOverdue = isCardOverdue(card, completedWorkLane);
  const assignees = cardAssignees(card);
  const cardId = card.post_id || card.postId;
  const attachCount = Array.isArray(card.attachments) ? card.attachments.length : 0;
  const priorityColors = {
    low: "bg-blue-100 text-blue-800",
    medium: "bg-yellow-100 text-yellow-800",
    high: "bg-orange-100 text-orange-800",
    urgent: "bg-red-100 text-red-800",
  };

  return (
    <Card
      id={kanbanCardAnchorId(cardId) || undefined}
      ref={setNodeRef}
      style={style}
      className={cn(
        "mb-2 cursor-pointer hover:shadow-md transition-shadow",
        isDragging && "opacity-50",
        isHighlighted && "ring-2 ring-primary shadow-md",
        frozen && "opacity-90 border-dashed",
        isOverdue && "border-destructive/50 bg-destructive/5"
      )}
      onClick={() => onCardClick(card)}
    >
      <CardContent className="p-3">
        <div className="flex items-start gap-2">
          {!listMode ? (
            <div
              {...(dragDisabled ? {} : attributes)}
              {...(dragDisabled ? {} : listeners)}
              className={cn(
                "text-muted-foreground",
                dragDisabled
                  ? "cursor-not-allowed opacity-40"
                  : "cursor-grab active:cursor-grabbing hover:text-foreground"
              )}
              aria-disabled={dragDisabled}
            >
              <GripVertical className="h-4 w-4" />
            </div>
          ) : null}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-1">
              <h4 className="font-medium text-sm line-clamp-2 flex-1">{card.title}</h4>
              {onCardEdit ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 shrink-0 md:h-6 md:w-6"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCardEdit(card);
                  }}
                  aria-label="Edit card"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
              ) : null}
            </div>
            {listMode &&
            canMoveCards &&
            !frozen &&
            Array.isArray(laneOptions) &&
            laneOptions.length > 0 &&
            onMoveToLane ? (
              <div
                className="mb-2"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <label className="sr-only" htmlFor={`move-lane-${cardId}`}>
                  Move to lane
                </label>
                <select
                  id={`move-lane-${cardId}`}
                  className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  value={currentLane}
                  onChange={(e) => {
                    const next = e.target.value;
                    if (next && next !== currentLane) {
                      onMoveToLane(card, next);
                    }
                  }}
                  aria-label="Move to lane"
                >
                  {laneOptions.map((lane) => (
                    <option key={lane} value={lane}>
                      {lane}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            {card.content && (
              <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                {plainExcerpt(card.content, 160)}
              </p>
            )}

            <div className="flex items-center gap-2 flex-wrap mt-2">
              <Badge variant="secondary" className="text-xs font-medium">
                {workKindLabel(card)}
              </Badge>
              <ParentBreadcrumb
                card={card}
                hierarchyCards={hierarchyCards}
                onFocusParent={onShowLinkedWork}
                onOpenRemoteParent={onOpenRemoteParent}
              />
              {storyPointsOf(card) != null ? (
                <Badge variant="outline" className="text-xs font-normal">
                  {storyPointsOf(card)} pts
                </Badge>
              ) : null}
              <LinkedDoneChip card={card} onShowLinkedWork={onShowLinkedWork} />
              {frozen && (
                <Badge variant="outline" className="text-xs">
                  Handoff pending
                </Badge>
              )}
              {isOverdue && (
                <Badge
                  variant="outline"
                  className="text-xs border-destructive/40 text-destructive"
                >
                  Overdue
                </Badge>
              )}
              {attachCount > 0 && (
                <Badge
                  variant="outline"
                  className="text-xs gap-0.5 font-normal text-muted-foreground"
                  title={`${attachCount} attachment${attachCount === 1 ? "" : "s"}`}
                >
                  <Paperclip className="h-3 w-3" />
                  {attachCount}
                </Badge>
              )}
              {card.priority && (
                <Badge
                  variant="outline"
                  className={cn("text-xs", priorityColors[card.priority] || "bg-gray-100 text-gray-800")}
                >
                  {card.priority}
                </Badge>
              )}
              {assignees.length > 0 && (
                <div
                  className="flex items-center gap-1"
                  title={assignees.map((a) => getFullName(a)).join(", ")}
                >
                  <div className="flex -space-x-1.5">
                    {assignees.slice(0, 3).map((a) => (
                      <OrgMemberAvatar
                        key={a.user_id || a.userId}
                        user={a}
                        className="h-5 w-5 ring-1 ring-background"
                        fallbackClassName="text-xs"
                      />
                    ))}
                  </div>
                  {assignees.length === 1 ? (
                    <span className="text-xs text-muted-foreground">
                      {getFullName(assignees[0])}
                    </span>
                  ) : assignees.length > 3 ? (
                    <span className="text-xs text-muted-foreground">
                      +{assignees.length - 3}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      {assignees.length}
                    </span>
                  )}
                </div>
              )}
              {due && (
                <div className={cn(
                  "flex items-center gap-1 text-xs",
                  isOverdue ? "text-destructive font-medium" : "text-muted-foreground"
                )}>
                  <Calendar className="h-3 w-3" />
                  <span>{due.toLocaleDateString()}</span>
                </div>
              )}
            </div>

            {card.tags && (
              <div className="flex flex-wrap gap-1 mt-2">
                {card.tags.split(',').slice(0, 2).map((tag, idx) => (
                  <Badge key={idx} variant="secondary" className="text-xs">
                    {tag.trim()}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const EpicStripCard = ({
  card,
  onOpenChildren,
  onCardEdit,
  onShowLinkedWork,
  isHighlighted,
  completedWorkLane = null,
}) => {
  const due = cardDueAt(card);
  const start = cardStartsAt(card);
  const { overdue } = epicHorizonOf(card, { completedWorkLane });
  const cardId = card.post_id || card.postId;

  return (
    <Card
      id={kanbanCardAnchorId(cardId) || undefined}
      className={cn(
        "mb-2 cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-primary bg-primary/5",
        isHighlighted && "ring-2 ring-primary shadow-md",
        overdue && "border-destructive/50"
      )}
      onClick={() => onOpenChildren?.(card)}
    >
      <CardContent className="p-3">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h4 className="font-medium text-sm line-clamp-2 flex-1 min-w-0">
            <span className="text-muted-foreground font-semibold mr-1">Epic</span>
            {card.title}
          </h4>
          {onCardEdit ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                onCardEdit(card);
              }}
              aria-label="Edit card"
            >
              <Pencil className="h-3 w-3" />
            </Button>
          ) : null}
        </div>
        <div className="flex items-center gap-2 flex-wrap mt-2">
          <LinkedDoneChip card={card} onShowLinkedWork={onShowLinkedWork} />
          {overdue ? (
            <Badge
              variant="outline"
              className="text-xs border-destructive/40 text-destructive"
            >
              Overdue
            </Badge>
          ) : null}
          {start || due ? (
            <div
              className={cn(
                "flex items-center gap-1 text-xs",
                overdue ? "text-destructive font-medium" : "text-muted-foreground"
              )}
            >
              <Calendar className="h-3 w-3" />
              <span>
                {start && due
                  ? `${start.toLocaleDateString()} – ${due.toLocaleDateString()}`
                  : (start || due).toLocaleDateString()}
              </span>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

const StubKanbanCard = ({ stub, onClick, onDismiss }) => {
  const teamName =
    stub?.current_team?.name ||
    stub?.currentTeam?.name ||
    "Another team";
  const handoffId = stub?.handoff_id ?? stub?.handoffId;

  return (
    <Card
      className="mb-2 cursor-pointer hover:shadow-md transition-shadow border-dashed opacity-90"
      onClick={() => onClick?.(stub)}
    >
      <CardContent className="p-3">
        <div className="flex items-start gap-2">
          <ArrowRightLeft className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-1">
              <h4 className="font-medium text-sm line-clamp-2">{stub.title}</h4>
              {onDismiss && handoffId != null ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
                  title="Remove from Handed off"
                  aria-label="Remove from Handed off"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDismiss(stub);
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              ) : null}
            </div>
            {stub.content && (
              <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                {plainExcerpt(stub.content, 160)}
              </p>
            )}
            <div className="flex items-center gap-2 flex-wrap mt-2">
              <Badge variant="secondary" className="text-xs">
                Now on {teamName}
              </Badge>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Kanban Column Component
const KanbanColumn = ({
  status,
  cards,
  onCardClick,
  onCardEdit,
  onShowLinkedWork,
  onOpenRemoteParent,
  highlightedCardId,
  feedSilent = false,
  isStubLane = false,
  onStubClick,
  onDismissStub,
  completedWorkLane = null,
  hierarchyCards = [],
  canMoveCards = true,
  listMode = false,
  laneOptions = null,
  onMoveToLane = null,
}) => {
  const sortableIds = isStubLane
    ? []
    : cards.map((card) => card.post_id || card.postId);
  const { setNodeRef, isOver } = useDroppable({
    id: status,
    disabled: isStubLane || !canMoveCards || listMode,
  });

  return (
    <div
      className={cn(
        listMode ? "w-full min-w-0" : "flex-shrink-0 w-64 snap-start"
      )}
    >
      <Card
        className={cn(
          "h-full border-t-2 border-t-primary flex flex-col",
          isOver && !isStubLane && !listMode && "ring-2 ring-primary ring-offset-2 ring-offset-background"
        )}
      >
        <CardHeader className="pb-3 bg-muted/50 rounded-t-lg shrink-0">
          <CardTitle className="text-sm font-semibold flex items-center justify-between gap-2">
            <span className="truncate">{status}</span>
            <div className="flex items-center gap-1 shrink-0">
              {feedSilent && (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge
                        variant="outline"
                        className="text-[10px] py-0 px-1.5 gap-1 font-normal text-muted-foreground cursor-help"
                      >
                        <EyeOff className="h-3 w-3" />
                        Hidden from feed
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs text-sm leading-snug">
                      {FEED_SILENT_TOOLTIP}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <Badge variant="secondary" className="text-xs">
                {cards.length}
              </Badge>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 pt-0 min-h-0 flex-1" ref={setNodeRef}>
          {isStubLane ? (
            <div
              className={cn(
                "min-h-[200px] overflow-y-auto overscroll-contain",
                listMode
                  ? "max-h-[min(36rem,calc(100dvh-14rem))]"
                  : "max-h-[min(28rem,calc(100dvh-16rem))]"
              )}
            >
              {cards.length === 0 ? (
                <div className="text-center text-sm text-muted-foreground py-8">
                  No handed-off cards
                </div>
              ) : (
                cards.map((stub) => {
                  const stubId = stub.post_id || stub.postId;
                  return (
                    <StubKanbanCard
                      key={`stub-${stubId}`}
                      stub={stub}
                      onClick={onStubClick}
                      onDismiss={onDismissStub}
                    />
                  );
                })
              )}
            </div>
          ) : (
            <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
              <div
                className={cn(
                  "min-h-[200px] overflow-y-auto overscroll-contain",
                  listMode
                    ? "max-h-[min(36rem,calc(100dvh-14rem))]"
                    : "max-h-[min(28rem,calc(100dvh-16rem))]"
                )}
              >
                {cards.length === 0 ? (
                  <div className="text-center text-sm text-muted-foreground py-8">
                    No cards
                  </div>
                ) : (
                  cards.map((card) => {
                    const cardId = card.post_id || card.postId;
                    const isHighlighted =
                      highlightedCardId != null &&
                      String(cardId) === String(highlightedCardId);
                    return (
                      <SortableKanbanCard
                        key={cardId}
                        card={card}
                        onCardClick={onCardClick}
                        onCardEdit={onCardEdit}
                        onShowLinkedWork={onShowLinkedWork}
                        onOpenRemoteParent={onOpenRemoteParent}
                        isHighlighted={isHighlighted}
                        completedWorkLane={completedWorkLane}
                        hierarchyCards={hierarchyCards}
                        canMoveCards={canMoveCards}
                        listMode={listMode}
                        laneOptions={laneOptions}
                        onMoveToLane={onMoveToLane}
                      />
                    );
                  })
                )}
              </div>
            </SortableContext>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const KanbanBoard = ({
  posts,
  allPosts = null,
  loading,
  onCardMove,
  onCreateCard,
  onCardClick,
  onCardEdit,
  canCreateCard,
  teamMemberIds = null,
  lanes = [], // Array of { lane_id, name, position }
  highlightedCardId = null,
  inspectCardId = null,
  inspectNonce = 0,
  onInspectDismissed,
  stubs = [],
  orgId = null,
  teamId = null,
  onStubClick,
  onDismissStub,
  completedWorkLane = null,
  defaultChannel = null,
  onViewTeamChannel,
}) => {
  const { accessToken } = useAuth();
  const isMobile = useIsMobile();
  const [activeId, setActiveId] = useState(null);
  const [cards, setCards] = useState([]);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [showEpics, setShowEpics] = useState(
    () => loadBoardWorkFilter(orgId, teamId).showEpics
  );
  const [epicFilter, setEpicFilter] = useState(
    () => loadBoardWorkFilter(orgId, teamId).epicFilter
  );
  const [linkedFocusId, setLinkedFocusId] = useState(null);
  const [epicChildrenCard, setEpicChildrenCard] = useState(null);
  const [overlayFocusId, setOverlayFocusId] = useState(null);
  const lastInspectTokenRef = useRef(null);
  const [remoteParentId, setRemoteParentId] = useState(null);
  const [mobileLane, setMobileLane] = useState(null);
  const canMoveCards = Boolean(onCardMove);

  useEffect(() => {
    const next = loadBoardWorkFilter(orgId, teamId);
    setShowEpics(next.showEpics);
    setEpicFilter(next.epicFilter);
  }, [orgId, teamId]);

  const persistWorkFilter = (nextShow, nextEpic) => {
    try {
      localStorage.setItem(
        boardWorkFilterKey(orgId, teamId),
        JSON.stringify({ showEpics: nextShow, epicFilter: nextEpic })
      );
    } catch {
      /* ignore quota / private mode */
    }
  };

  // Use provided lanes or fallback to default
  const kanbanStatuses = lanes.length > 0 
    ? lanes.map(lane => lane.name).sort((a, b) => {
        const laneA = lanes.find(l => l.name === a);
        const laneB = lanes.find(l => l.name === b);
        return (laneA?.position ?? 0) - (laneB?.position ?? 0);
      })
    : DEFAULT_KANBAN_STATUSES;

  const mobileLaneOptions = useMemo(() => {
    const opts = [...kanbanStatuses];
    if ((stubs || []).length > 0) opts.push(HANDED_OFF_LANE);
    return opts;
  }, [kanbanStatuses, stubs]);

  useEffect(() => {
    if (!mobileLaneOptions.length) return;
    setMobileLane((prev) =>
      prev && mobileLaneOptions.includes(prev) ? prev : mobileLaneOptions[0]
    );
  }, [mobileLaneOptions]);

  const moveCardToLane = useCallback(
    (card, newStatus) => {
      if (!onCardMove || !card || !newStatus) return;
      if (newStatus === HANDED_OFF_LANE) return;
      const cardId = card.post_id || card.postId;
      if (isCardFrozen(card)) {
        toastError("This card is frozen while a handoff is pending.");
        return;
      }
      setCards((prev) => {
        const columnCards = prev.filter((c) => {
          const id = c.post_id || c.postId;
          const lane = c.kanbanStatus || c.kanban_status;
          return id !== cardId && lane === newStatus;
        });
        let newPosition = 0;
        if (columnCards.length > 0) {
          const lastCard = columnCards[columnCards.length - 1];
          newPosition =
            (lastCard.kanbanPosition || lastCard.kanban_position || 0) + 1;
        }
        onCardMove(cardId, newStatus, newPosition);
        return prev.map((c) =>
          (c.post_id || c.postId) === cardId
            ? {
                ...c,
                kanbanStatus: newStatus,
                kanban_status: newStatus,
                kanbanPosition: newPosition,
                kanban_position: newPosition,
              }
            : c
        );
      });
      if (isMobile) setMobileLane(newStatus);
    },
    [onCardMove, isMobile]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Avoid stealing trackpad/mouse gestures meant for board scrolling
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const boardScrollRef = useRef(null);

  // Keep horizontal trackpad/wheel gestures on the board so nested page scroll
  // doesn't "stick" when scrolling toward the Handed off lane.
  useEffect(() => {
    const el = boardScrollRef.current;
    if (!el) return undefined;

    const onWheel = (e) => {
      const absX = Math.abs(e.deltaX);
      const absY = Math.abs(e.deltaY);
      // Horizontal-dominant (or shift+wheel) → scroll the board exclusively
      const horizontalIntent = absX > absY || e.shiftKey;
      if (!horizontalIntent) return;

      const delta = e.shiftKey && absX < absY ? e.deltaY : e.deltaX || e.deltaY;
      if (delta === 0) return;

      const maxScroll = el.scrollWidth - el.clientWidth;
      if (maxScroll <= 0) return;

      const next = Math.min(maxScroll, Math.max(0, el.scrollLeft + delta));
      if (next !== el.scrollLeft) {
        e.preventDefault();
        el.scrollLeft = next;
      }
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [loading, kanbanStatuses.length, stubs?.length]);


  const hierarchyCards = allPosts?.length ? allPosts : cards;

  const openCardOverlay = useCallback(
    (card) => {
      if (!card) return;
      const pool = allPosts?.length ? allPosts : cards;
      const focusId = postIdOfWork(card);
      if (workKindOf(card) === "epic") {
        const loaded =
          (pool || []).find((c) => postIdOfWork(c) === focusId) || card;
        setEpicChildrenCard(loaded);
        setOverlayFocusId(focusId);
        return;
      }
      const chain = parentChainOf(card, pool);
      for (const ancestor of chain) {
        if (workKindOf(ancestor) !== "epic") continue;
        const eid = postIdOfWork(ancestor);
        const loaded = (pool || []).find(
          (c) =>
            postIdOfWork(c) === eid &&
            (c.kanbanStatus || c.kanban_status)
        );
        if (loaded) {
          setEpicChildrenCard(loaded);
          setOverlayFocusId(focusId);
          return;
        }
      }
      setEpicChildrenCard(card);
      setOverlayFocusId(focusId);
    },
    [allPosts, cards]
  );

  useEffect(() => {
    if (inspectCardId == null || inspectCardId === "") return;
    const token = inspectNonce;
    if (lastInspectTokenRef.current === token && token !== 0) return;
    const pool = allPosts?.length ? allPosts : cards;
    const card = (pool || []).find(
      (c) => String(postIdOfWork(c)) === String(inspectCardId)
    );
    if (!card) return;
    lastInspectTokenRef.current = token;
    openCardOverlay(card);
  }, [inspectCardId, inspectNonce, allPosts, cards, openCardOverlay]);
  const epicOptions = useMemo(
    () =>
      (hierarchyCards || []).filter((c) => workKindOf(c) === "epic"),
    [hierarchyCards]
  );

  const linkedFocusCard = useMemo(() => {
    if (!linkedFocusId) return null;
    return (
      (hierarchyCards || []).find((c) => postIdOfWork(c) === linkedFocusId) ||
      cards.find((c) => postIdOfWork(c) === linkedFocusId) ||
      null
    );
  }, [linkedFocusId, hierarchyCards, cards]);

  const linkedFocusList = useMemo(() => {
    if (!linkedFocusCard) return [];
    const pool = hierarchyCards?.length ? hierarchyCards : cards;
    return sortLinkedWork(linkedWorkSet(linkedFocusCard, pool));
  }, [linkedFocusCard, hierarchyCards, cards]);

  const linkedMissingCount = useMemo(() => {
    if (!linkedFocusCard) return 0;
    const counts = childCountsOf(linkedFocusCard);
    if (!counts) return 0;
    const focusId = postIdOfWork(linkedFocusCard);
    const pool = hierarchyCards?.length ? hierarchyCards : cards;
    const directFound = pool.filter((c) => parentIdOf(c) === focusId).length;
    return Math.max(0, counts.total - directFound);
  }, [linkedFocusCard, hierarchyCards, cards]);

  const stripEpics = useMemo(() => {
    if (!showEpics) return [];
    const pool = hierarchyCards?.length ? hierarchyCards : cards;
    let epics = pool.filter((c) => workKindOf(c) === "epic");
    if (overdueOnly) {
      epics = epics.filter((c) => isCardOverdue(c, completedWorkLane));
    }
    if (linkedFocusId) {
      const focus = linkedFocusCard;
      const ancestorIds = new Set(
        focus ? parentChainOf(focus, pool).map((p) => postIdOfWork(p)) : []
      );
      epics = epics.filter((c) => {
        const id = postIdOfWork(c);
        if (id === linkedFocusId) return true;
        return ancestorIds.has(id);
      });
    } else if (epicFilter === "unparented") {
      epics = [];
    } else if (epicFilter && epicFilter !== "all") {
      const eid = Number(epicFilter);
      epics = epics.filter((c) => postIdOfWork(c) === eid);
    }
    return epics;
  }, [
    showEpics,
    hierarchyCards,
    cards,
    overdueOnly,
    completedWorkLane,
    linkedFocusId,
    linkedFocusCard,
    epicFilter,
  ]);

  const epicsByHorizon = useMemo(() => {
    const grouped = { now: [], upcoming: [], done: [] };
    for (const epic of stripEpics) {
      const { horizon } = epicHorizonOf(epic, { completedWorkLane });
      if (grouped[horizon]) grouped[horizon].push(epic);
      else grouped.now.push(epic);
    }
    return grouped;
  }, [stripEpics, completedWorkLane]);

  // Group and sort cards by status (stories/tasks only — epics live in the strip)
  const cardsByStatus = useMemo(() => {
    let source = overdueOnly
      ? cards.filter((c) => isCardOverdue(c, completedWorkLane))
      : cards;
    if (linkedFocusId) {
      const pool = hierarchyCards?.length ? hierarchyCards : cards;
      source = pool.filter((c) => {
        const id = postIdOfWork(c);
        if (id === linkedFocusId) return true;
        return isUnderWorkParent(c, linkedFocusId, pool);
      });
      if (overdueOnly) {
        source = source.filter((c) => isCardOverdue(c, completedWorkLane));
      }
    } else {
      if (epicFilter === "unparented") {
        source = source.filter(
          (c) => workKindOf(c) !== "epic" && !parentIdOf(c)
        );
      } else if (epicFilter && epicFilter !== "all") {
        const eid = Number(epicFilter);
        source = source.filter((c) => {
          if (workKindOf(c) === "epic") return false;
          return isUnderEpic(c, eid, hierarchyCards);
        });
      }
    }
    source = source.filter((c) => workKindOf(c) !== "epic");
    const grouped = {};
    kanbanStatuses.forEach((status) => {
      grouped[status] = [];
    });

    source.forEach((card) => {
      const status = card.kanbanStatus || card.kanban_status;
      if (status && grouped[status]) {
        grouped[status].push(card);
      }
    });

    // Sort each group by kanban_position
    Object.keys(grouped).forEach((status) => {
      grouped[status].sort((a, b) => {
        const posA = a.kanbanPosition || a.kanban_position || 0;
        const posB = b.kanbanPosition || b.kanban_position || 0;
        return posA - posB;
      });
    });

    return grouped;
  }, [
    cards,
    kanbanStatuses,
    overdueOnly,
    completedWorkLane,
    epicFilter,
    hierarchyCards,
    linkedFocusId,
  ]);

  // Update cards when posts change
  React.useEffect(() => {
    // Filter posts that have kanban_status (are kanban cards)
    const kanbanCards = posts.filter(
      (post) => post.kanbanStatus || post.kanban_status
    );
    setCards(kanbanCards);
  }, [posts]);

  const handleStubClick = (stub) => {
    if (onStubClick) {
      onStubClick(stub);
      return;
    }
    const currentTeam = stub?.current_team || stub?.currentTeam;
    const targetTeamId = currentTeam?.team_id ?? currentTeam?.teamId;
    const postId = stub?.post_id ?? stub?.postId;
    if (!targetTeamId || !postId) return;
    const params = new URLSearchParams();
    if (orgId) params.set("org_id", orgId);
    params.set("tab", "kanban");
    params.set("card", String(postId));
    window.location.assign(`/teams/${targetTeamId}?${params.toString()}`);
  };

  const handleDragStart = (event) => {
    if (!canMoveCards) return;
    const card = cards.find(
      (c) => (c.post_id || c.postId) === event.active.id
    );
    if (card && isCardFrozen(card)) {
      return;
    }
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!canMoveCards) return;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    // Find the card being dragged
    const activeCard = cards.find(
      (c) => (c.post_id || c.postId) === activeId
    );
    if (!activeCard) return;

    if (isCardFrozen(activeCard)) {
      toastError("This card is frozen while a handoff is pending.");
      return;
    }

    if (overId === HANDED_OFF_LANE) {
      return;
    }

    // Check if dropping on a column (status) or another card
    const overColumn = kanbanStatuses.find((status) => overId === status);
    const overCard = cards.find((c) => (c.post_id || c.postId) === overId);

    let newStatus = activeCard.kanbanStatus || activeCard.kanban_status;
    let newPosition = activeCard.kanbanPosition || activeCard.kanban_position || 0;

    if (overColumn) {
      // Dropped on a column
      newStatus = overColumn;
      // Position at the end of the column
      const columnCards = cardsByStatus[overColumn] || [];
      if (columnCards.length > 0) {
        const lastCard = columnCards[columnCards.length - 1];
        newPosition = (lastCard.kanbanPosition || lastCard.kanban_position || 0) + 1;
      } else {
        newPosition = 0;
      }
    } else if (overCard) {
      // Dropped on another card
      const overStatus = overCard.kanbanStatus || overCard.kanban_status;
      const overPosition = overCard.kanbanPosition || overCard.kanban_position || 0;

      newStatus = overStatus;
      // Position after the target card
      newPosition = overPosition + 0.5;
    }

    // Update local state optimistically
    const updatedCards = cards.map((card) => {
      if ((card.post_id || card.postId) === activeId) {
        return {
          ...card,
          kanbanStatus: newStatus,
          kanban_status: newStatus,
          kanbanPosition: newPosition,
          kanban_position: newPosition,
        };
      }
      return card;
    });
    setCards(updatedCards);

    // Call API to update
    if (onCardMove) {
      onCardMove(activeId, newStatus, newPosition);
    }
  };

  const activeCard = activeId
    ? cards.find((c) => (c.post_id || c.postId) === activeId)
    : null;

  if (loading) {
    return (
      <div className="w-full min-w-0 max-w-full overflow-x-auto overflow-y-hidden overscroll-x-contain snap-x snap-mandatory pb-4">
        <div className="flex w-max gap-4">
          {kanbanStatuses.map((status) => (
            <div key={status} className="flex-shrink-0 w-64 snap-start">
              <Card>
                <CardHeader>
                  <Skeleton className="h-6 w-24" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-32 mb-2" />
                  <Skeleton className="h-32 mb-2" />
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 w-full min-w-0 max-w-full">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <h3 className="text-lg font-semibold">Kanban Board</h3>
          <Button
            type="button"
            variant={overdueOnly ? "default" : "outline"}
            size="sm"
            className="h-10 text-xs md:h-7"
            onClick={() => setOverdueOnly((v) => !v)}
            title="Show only overdue cards"
          >
            Overdue only
          </Button>
          <Button
            type="button"
            variant={showEpics ? "default" : "outline"}
            size="sm"
            className="h-10 text-xs md:h-7"
            onClick={() =>
              setShowEpics((v) => {
                const next = !v;
                persistWorkFilter(next, epicFilter);
                return next;
              })
            }
            title="Show epics above the board"
          >
            Show epics
          </Button>
          <select
            className="h-10 min-w-0 max-w-[14rem] rounded-md border border-input bg-background px-2 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:h-7"
            value={epicFilter}
            onChange={(e) => {
              const next = e.target.value;
              setEpicFilter(next);
              persistWorkFilter(showEpics, next);
            }}
            aria-label="Filter by epic"
          >
            <option value="all">All epics</option>
            <option value="unparented">Unparented</option>
            {epicOptions.map((epic) => {
              const id = postIdOfWork(epic);
              return (
                <option key={id} value={String(id)}>
                  {epic.title}
                </option>
              );
            })}
          </select>
        </div>
        {canCreateCard && (
          <Button
            type="button"
            onClick={onCreateCard}
            size="sm"
            className="shrink-0"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Card
          </Button>
        )}
      </div>
      {linkedFocusCard ? (
        <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm space-y-2">
          <div className="flex items-start justify-between gap-2">
            <p className="min-w-0 text-muted-foreground">
              Showing {linkedFocusList.length} card
              {linkedFocusList.length === 1 ? "" : "s"} linked to{" "}
              <span className="font-medium text-foreground">
                {linkedFocusCard.title}
              </span>
              . Includes cards outside the current sprint filter.
            </p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 shrink-0"
              onClick={() => setLinkedFocusId(null)}
            >
              <X className="h-3.5 w-3.5 mr-1" />
              Clear
            </Button>
          </div>
          <ul className="space-y-1 max-h-40 overflow-y-auto">
            {linkedFocusList.map((c) => {
              const id = postIdOfWork(c);
              const lane = cardLaneOf(c);
              return (
                <li
                  key={id}
                  className="flex items-baseline gap-2 text-xs min-w-0"
                >
                  <Badge variant="secondary" className="text-[10px] shrink-0">
                    {workKindLabel(c)}
                  </Badge>
                  <button
                    type="button"
                    className="font-medium text-foreground truncate hover:underline text-left"
                    onClick={() => openCardOverlay(c)}
                  >
                    {c.title || "Untitled"}
                  </button>
                  {lane ? (
                    <span className="text-muted-foreground shrink-0">{lane}</span>
                  ) : null}
                </li>
              );
            })}
          </ul>
          {linkedMissingCount > 0 ? (
            <p className="text-xs text-muted-foreground">
              {linkedMissingCount} linked card
              {linkedMissingCount === 1 ? "" : "s"} are not loaded on this board.
            </p>
          ) : null}
        </div>
      ) : null}

      {showEpics ? (
        <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-2">
          <div className="flex items-baseline justify-between gap-2 flex-wrap">
            <h4 className="text-sm font-semibold">Epics</h4>
            <p className="text-xs text-muted-foreground">
              Epics are not sprint-scoped.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {EPIC_HORIZON_STRIPS.map(({ key, label }) => {
              const list = epicsByHorizon[key] || [];
              return (
                <div key={key} className="min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {label}
                    </span>
                    <Badge variant="secondary" className="text-[10px]">
                      {list.length}
                    </Badge>
                  </div>
                  {list.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-2">None</p>
                  ) : (
                    list.map((epic) => {
                      const epicId = postIdOfWork(epic);
                      const isHighlighted =
                        (highlightedCardId != null &&
                          String(epicId) === String(highlightedCardId)) ||
                        epicId === linkedFocusId;
                      return (
                        <EpicStripCard
                          key={epicId}
                          card={epic}
                          onOpenChildren={openCardOverlay}
                          onCardEdit={onCardEdit}
                          onShowLinkedWork={(c) =>
                            setLinkedFocusId(postIdOfWork(c))
                          }
                          isHighlighted={isHighlighted}
                          completedWorkLane={completedWorkLane}
                        />
                      );
                    })
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {isMobile ? (
        <div className="space-y-3 w-full min-w-0">
          <div className="flex items-center gap-2">
            <label className="sr-only" htmlFor="kanban-mobile-lane">
              Lane
            </label>
            <select
              id="kanban-mobile-lane"
              className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm font-medium ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={mobileLane || mobileLaneOptions[0] || ""}
              onChange={(e) => setMobileLane(e.target.value)}
              aria-label="Kanban lane"
            >
              {mobileLaneOptions.map((lane) => {
                const count =
                  lane === HANDED_OFF_LANE
                    ? (stubs || []).length
                    : (cardsByStatus[lane] || []).length;
                return (
                  <option key={lane} value={lane}>
                    {lane} ({count})
                  </option>
                );
              })}
            </select>
          </div>
          <DndContext sensors={sensors} collisionDetection={closestCorners}>
            {mobileLane === HANDED_OFF_LANE ? (
              <KanbanColumn
                status={HANDED_OFF_LANE}
                cards={stubs || []}
                isStubLane
                listMode
                onStubClick={handleStubClick}
                onDismissStub={onDismissStub}
              />
            ) : (
              <KanbanColumn
                status={mobileLane || kanbanStatuses[0]}
                cards={cardsByStatus[mobileLane || kanbanStatuses[0]] || []}
                onCardClick={openCardOverlay}
                onCardEdit={onCardEdit}
                canMoveCards={canMoveCards}
                listMode
                laneOptions={kanbanStatuses}
                onMoveToLane={moveCardToLane}
                onShowLinkedWork={(card) =>
                  setLinkedFocusId(postIdOfWork(card))
                }
                onOpenRemoteParent={(parent) => {
                  const id = postIdOfWork(parent);
                  if (id) setRemoteParentId(id);
                }}
                highlightedCardId={highlightedCardId}
                feedSilent={Boolean(
                  lanes.find((l) => l.name === mobileLane)?.feed_silent ??
                    lanes.find((l) => l.name === mobileLane)?.feedSilent
                )}
                completedWorkLane={completedWorkLane}
                hierarchyCards={hierarchyCards}
              />
            )}
          </DndContext>
        </div>
      ) : (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div
          ref={boardScrollRef}
          className="w-full min-w-0 max-w-full overflow-x-auto overflow-y-hidden overscroll-x-contain snap-x snap-mandatory pb-4"
        >
          <div className="flex w-max gap-4">
            {kanbanStatuses.map((status) => {
              const laneMeta = lanes.find((l) => l.name === status);
              const feedSilent = Boolean(
                laneMeta?.feed_silent ?? laneMeta?.feedSilent
              );
              return (
                <KanbanColumn
                  key={status}
                  status={status}
                  cards={cardsByStatus[status] || []}
                  onCardClick={openCardOverlay}
                  onCardEdit={onCardEdit}
                  canMoveCards={canMoveCards}
                  onShowLinkedWork={(card) =>
                    setLinkedFocusId(postIdOfWork(card))
                  }
                  onOpenRemoteParent={(parent) => {
                    const id = postIdOfWork(parent);
                    if (id) setRemoteParentId(id);
                  }}
                  highlightedCardId={highlightedCardId}
                  feedSilent={feedSilent}
                  completedWorkLane={completedWorkLane}
                  hierarchyCards={hierarchyCards}
                />
              );
            })}
            <KanbanColumn
              key={HANDED_OFF_LANE}
              status={HANDED_OFF_LANE}
              cards={stubs || []}
              isStubLane
              onStubClick={handleStubClick}
              onDismissStub={onDismissStub}
            />
          </div>
        </div>
        <DragOverlay>
          {activeCard ? (
            <Card className="w-64 opacity-90 shadow-lg">
              <CardContent className="p-3">
                <h4 className="font-medium text-sm mb-1">{activeCard.title}</h4>
                {activeCard.content && (
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {plainExcerpt(activeCard.content, 160)}
                  </p>
                )}
              </CardContent>
            </Card>
          ) : null}
        </DragOverlay>
      </DndContext>
      )}
      <CardInspectOverlay
        epic={epicChildrenCard}
        hierarchyCards={hierarchyCards}
        completedWorkLane={completedWorkLane}
        orgId={orgId}
        focusId={overlayFocusId}
        onOpenChange={(open) => {
          if (!open) {
            setEpicChildrenCard(null);
            setOverlayFocusId(null);
            onInspectDismissed?.();
          }
        }}
        onCardClick={onCardClick}
        onCardEdit={onCardEdit}
        onOpenRemoteParent={(parent) => {
          const id = postIdOfWork(parent);
          if (id) setRemoteParentId(id);
        }}
        defaultChannel={defaultChannel}
        teamId={teamId}
        teamMemberIds={teamMemberIds}
        onViewTeamChannel={onViewTeamChannel}
      />
      <LinkedParentDialog
        open={Boolean(remoteParentId)}
        parentId={remoteParentId}
        orgId={orgId}
        accessToken={accessToken}
        onClose={() => setRemoteParentId(null)}
      />
    </div>
  );
};

export default KanbanBoard;
